import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, InputNumber, Button, Card, Select, Checkbox, message, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  Soup,
  UpdateSoupRequest,
  CreateExchangeRequest,
  CreateFilterRequest,
} from '../../types/api.types';
import { useDataContext } from '../../contexts/DataContext';
import ApiService from '../../services/api';
import { MESSAGES, FILTER_LABELS } from '../../utils/constants';

const { Text } = Typography;

interface EditSoupModalProps {
  visible: boolean;
  soup: Soup | null;
  fiatName: string;
  onCancel: () => void;
  onSuccess: () => void;
}

interface ExchangeFormData extends Omit<CreateExchangeRequest, 'filter_buy' | 'filter_sell'> {
  id?: number;
  useDifferentFilters: boolean;
  filter_buy: CreateFilterRequest;
  filter_sell: CreateFilterRequest;
}

const EditSoupModal: React.FC<EditSoupModalProps> = ({
  visible,
  soup,
  fiatName,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [average, setAverage] = useState<number | null>(0);
  const [exchanges, setExchanges] = useState<ExchangeFormData[]>([]);
  const [availableExchanges, setAvailableExchanges] = useState<string[]>([]);
  const [availableBanks, setAvailableBanks] = useState<Record<string, string[]>>({});
  const [loadingBanks, setLoadingBanks] = useState<Record<string, boolean>>({});
  const [addExchangeModalVisible, setAddExchangeModalVisible] = useState(false);
  const [addExchangeForm] = Form.useForm();
  const [newExchangeUseDifferentFilters, setNewExchangeUseDifferentFilters] = useState(false);
  const { loadFiatSettings } = useDataContext();

  useEffect(() => {
    if (visible && soup) {
      setName(soup.name);
      setAverage(soup.average);
      setExchanges(
        soup.exchanges.map(ex => ({
          id: ex.id,
          exchange_name: ex.exchange.name,
          banks: ex.banks.map(b => b.name),
          useDifferentFilters: JSON.stringify(ex.filter_buy) !== JSON.stringify(ex.filter_sell),
          filter_buy: ex.filter_buy,
          filter_sell: ex.filter_sell,
        }))
      );
      loadExchangesList();
      soup.exchanges.forEach(ex => {
        loadBanksList(ex.exchange.name);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, soup]);

  const loadExchangesList = async () => {
    try {
      const exchangesList = await ApiService.getExchanges(fiatName);
      setAvailableExchanges(exchangesList);
    } catch (error) {
      console.error('Ошибка загрузки списка бирж:', error);
    }
  };

  const loadBanksList = async (exchangeName: string) => {
    if (loadingBanks[exchangeName] || availableBanks[exchangeName]) return;
    setLoadingBanks(prev => ({ ...prev, [exchangeName]: true }));
    try {
      const banks = await ApiService.getBanks(fiatName, exchangeName);
      setAvailableBanks(prev => ({ ...prev, [exchangeName]: banks }));
    } catch (error) {
      console.error(`Ошибка загрузки банков для ${exchangeName}:`, error);
    } finally {
      setLoadingBanks(prev => ({ ...prev, [exchangeName]: false }));
    }
  };

  const handleUpdateSoupDetails = async () => {
    try {
      await form.validateFields(['name', 'average']);
      setLoading(true);
      const updateData: UpdateSoupRequest = { name, average: average ?? 0 };
      await ApiService.updateSoup(soup!.id, updateData);
      message.success('Информация о супе обновлена');
      await loadFiatSettings(fiatName);
    } catch (error) {
      console.error('Ошибка обновления супа:', error);
      message.error(MESSAGES.ERROR.GENERIC);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExchange = () => {
    setAddExchangeModalVisible(true);
    addExchangeForm.resetFields();
    setNewExchangeUseDifferentFilters(false);
  };

  const handleSaveNewExchange = async () => {
    if (!soup) return;
    try {
      const values = await addExchangeForm.validateFields();
      setLoading(true);

      const filterBuy: CreateFilterRequest = {
        month_order: values.filter_buy_month_order,
        month_finish_rate: values.filter_buy_month_finish_rate,
        max_low_single_trans_amount: values.filter_buy_max_low_single_trans_amount,
        min_high_single_trans_amount: values.filter_buy_min_high_single_trans_amount,
        average_size: values.filter_buy_average_size,
      };

      const filterSell: CreateFilterRequest = newExchangeUseDifferentFilters ? {
        month_order: values.filter_sell_month_order,
        month_finish_rate: values.filter_sell_month_finish_rate,
        max_low_single_trans_amount: values.filter_sell_max_low_single_trans_amount,
        min_high_single_trans_amount: values.filter_sell_min_high_single_trans_amount,
        average_size: values.filter_sell_average_size,
      } : filterBuy;

      const newExchangeData: CreateExchangeRequest = {
        exchange_name: values.exchange_name,
        banks: values.banks,
        filter_buy: filterBuy,
        filter_sell: filterSell,
      };

      await ApiService.createExchange(soup!.id, newExchangeData);
      message.success('Биржа успешно добавлена');
      setAddExchangeModalVisible(false);
      await loadFiatSettings(fiatName);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Ошибка добавления биржи:', error);
      message.error('Не удалось добавить биржу');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveExchange = async (exchangeId: number) => {
    try {
      setLoading(true);
      await ApiService.deleteExchange(exchangeId);
      message.success('Биржа удалена');
      await loadFiatSettings(fiatName);
    } catch (error) {
      console.error('Ошибка удаления биржи:', error);
      message.error(MESSAGES.ERROR.GENERIC);
    } finally {
      setLoading(false);
    }
  };

  /*
  const handleUpdateExchange = async (exchangeId: number, data: any) => {
     // This is a placeholder for editing an exchange within the soup.
     // A separate, more detailed modal (`EditExchangeModal`) would be better for this.
     message.info('Редактирование биржи в супе еще не реализовано. Используйте отдельное редактирование бирж.');
  };
  */


  const handleCancel = () => {
    onCancel();
  };

  return (
    <>
      <Modal
        title="Редактировать суп"
        open={visible}
        onOk={handleUpdateSoupDetails}
        onCancel={handleCancel}
        confirmLoading={loading}
        destroyOnClose
        width="90%"
        style={{ maxWidth: 800 }}
        centered
        footer={[
          <Button key="back" onClick={onCancel}>
            Отмена
          </Button>,
          <Button key="submit" type="primary" loading={loading} onClick={handleUpdateSoupDetails}>
            Сохранить
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" initialValues={{ name, average }}>
          <Card title="Основная информация">
            <Form.Item name="name" label="Название супа" rules={[{ required: true, message: 'Введите название' }]}>
              <Input value={name} onChange={e => setName(e.target.value)} />
            </Form.Item>
            <Form.Item name="average" label="Среднее значение" rules={[{ required: true, message: 'Введите среднее значение' }]}>
              <InputNumber value={average} onChange={setAverage} style={{ width: '100%' }} min={0} precision={0} />
            </Form.Item>
          </Card>
        </Form>
        <Card
          title="Биржи в супе"
          style={{ marginTop: 16 }}
          extra={<Button onClick={handleAddExchange} icon={<PlusOutlined />}>Добавить биржу</Button>}
        >
          {exchanges.map((exchange, index) => (
            <Card key={exchange.id || index} size="small" style={{ marginBottom: 16 }}
              title={exchange.exchange_name || 'Новая биржа'}
              extra={<Button icon={<DeleteOutlined />} danger onClick={() => handleRemoveExchange(exchange.id!)} />}
            >
              <p>Банки: {exchange.banks.join(', ')}</p>
              <Checkbox checked={exchange.useDifferentFilters} disabled>Разные фильтры для покупки и продажи</Checkbox>
               {/* Details of filters can be displayed here if needed */}
            </Card>
          ))}
        </Card>
      </Modal>

      <Modal
        title="Добавить биржу"
        open={addExchangeModalVisible}
        onOk={handleSaveNewExchange}
        onCancel={() => setAddExchangeModalVisible(false)}
        confirmLoading={loading}
        width={800}
        destroyOnClose
      >
        <Form form={addExchangeForm} layout="vertical">
          <Form.Item
            name="exchange_name"
            label="Биржа"
            rules={[{ required: true, message: 'Выберите биржу' }]}
          >
            <Select
              placeholder="Выберите биржу"
              onChange={(value) => loadBanksList(value)}
            >
              {availableExchanges
                .filter(ex => !exchanges.some(existing => existing.exchange_name === ex))
                .map(ex => (
                  <Select.Option key={ex} value={ex}>{ex}</Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.exchange_name !== currentValues.exchange_name}
          >
            {({ getFieldValue }) => {
              const exchangeName = getFieldValue('exchange_name');
              return exchangeName ? (
                <Form.Item
                  name="banks"
                  label="Банки"
                  rules={[{ required: true, message: 'Выберите банки' }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Выберите банки"
                    loading={loadingBanks[exchangeName]}
                  >
                    {(availableBanks[exchangeName] || []).map(bank => (
                      <Select.Option key={bank} value={bank}>{bank}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null;
            }}
          </Form.Item>

          <Card size="small" title="Фильтры">
            <Form.Item>
              <Checkbox
                checked={newExchangeUseDifferentFilters}
                onChange={(e) => setNewExchangeUseDifferentFilters(e.target.checked)}
              >
                Использовать разные фильтры для покупки и продажи
              </Checkbox>
            </Form.Item>

            <div style={{ display: 'grid', gridTemplateColumns: newExchangeUseDifferentFilters ? '1fr 1fr' : '1fr', gap: 16 }}>
              <div>
                <Text strong>{newExchangeUseDifferentFilters ? 'Покупка (Buy)' : 'Общие настройки'}</Text>
                {Object.entries(FILTER_LABELS).map(([key, label]) => (
                  <Form.Item
                    key={`buy_${key}`}
                    name={`filter_buy_${key}`}
                    label={label}
                    rules={[{ required: true, message: 'Обязательное поле' }]}
                    initialValue={0}
                  >
                    <InputNumber style={{ width: '100%' }} />
                  </Form.Item>
                ))}
              </div>

              {newExchangeUseDifferentFilters && (
                <div>
                  <Text strong>Продажа (Sell)</Text>
                  {Object.entries(FILTER_LABELS).map(([key, label]) => (
                    <Form.Item
                      key={`sell_${key}`}
                      name={`filter_sell_${key}`}
                      label={label}
                      rules={[{ required: true, message: 'Обязательное поле' }]}
                      initialValue={0}
                    >
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </Form>
      </Modal>
    </>
  );
};

export default EditSoupModal;
